import type {
  RumActionEventDomainContext,
  RumErrorEventDomainContext,
  RumEvent,
  RumFetchResourceEventDomainContext,
  RumLongTaskEventDomainContext,
  RumOtherResourceEventDomainContext,
  RumViewEventDomainContext,
  RumXhrResourceEventDomainContext,
} from '@datadog/browser-rum';

export type GetSessionId = () => string | undefined;

/**
 * DataDog RUM accepts a `beforeSend` hook,
 * allowing us to mutate outgoing RUM events
 * and/or call side effects based on RUM events.
 */
export type DatadogRumBeforeSend = (
  event: RumEvent,
  context?:
    | RumViewEventDomainContext
    | RumActionEventDomainContext
    | RumFetchResourceEventDomainContext
    | RumXhrResourceEventDomainContext
    | RumOtherResourceEventDomainContext
    | RumErrorEventDomainContext
    | RumLongTaskEventDomainContext,
) => void;

/**
 * Delete any token of the `/reset-password` page
 * from the DataDog RUM event's referrer URL property.
 *
 * Not doing so has potential security implications.
 *
 * @param event Event provided by DataDog RUM prior to sending.
 */
const removeResetPasswordToken = (event: RumEvent): void => {
  if (typeof event.view.referrer !== 'undefined') {
    try {
      const url = new URL(event.view.referrer);
      if (url.pathname === '/reset-password') {
        url.searchParams.delete('token');
        event.view.referrer = url.href;
      }
    } catch {
      // referrer could be Invalid URL.
    }
  }
};

/**
 * Add the WebRTC session ID as a mock URL parameter,
 * so we can match up provider and patient RUM sessions.
 *
 * @param getSessionId Function to get the current WebRTC session ID.
 * @param event Event provided by DataDog RUM prior to sending.
 */
const addWebRTCSessionId = (
  getSessionId: GetSessionId,
  event: RumEvent,
): void => {
  /**
   * Set @view.url_query.sessionid to WebRTC sessionId.
   */
  if (event.type === 'view') {
    const viewUrl = new URL(event.view.url);
    const sessionId = getSessionId();
    if (
      viewUrl.pathname.includes('/call') &&
      typeof sessionId !== 'undefined'
    ) {
      viewUrl.searchParams.set('sessionId', sessionId);
      event.view.url = viewUrl.toString();
    }
  }
};

/**
 * beforeSend hook for DataDog RUM.
 *
 * @param getSessionId Function to get the current WebRTC session ID.
 * @param event Event provided by DataDog RUM prior to sending.
 * @returns void
 */
export const createDatadogRumBeforeSend =
  (getSessionId: GetSessionId): DatadogRumBeforeSend =>
  (event) => {
    removeResetPasswordToken(event);
    addWebRTCSessionId(getSessionId, event);
  };
